import type { RealtimeChannel } from "@supabase/supabase-js";

import { queryClient } from "../../lib/queryClient";
import { getSupabase } from "../../lib/supabase";
import { healthMonitor } from "./HealthMonitor";

export class ChannelManager {
  private cellChannels = new Map<string, RealtimeChannel>();
  private stationChannels = new Map<string, RealtimeChannel>();
  private stationRefCounts = new Map<string, number>();
  private favoriteUnsubs = new Map<string, () => void>();
  private communityFeedChannel: RealtimeChannel | null = null;
  private communityFeedRefCount = 0;
  private communityFeedHealthUnsub: (() => void) | null = null;

  setActiveCells(cells: string[]): void {
    const next = new Set(cells);
    const current = new Set(this.cellChannels.keys());

    for (const cell of next) {
      if (!current.has(cell)) this.subscribeToCell(cell);
    }
    for (const cell of current) {
      if (!next.has(cell)) this.unsubscribeFromCell(cell);
    }
  }

  subscribeToStation(stationId: string): () => void {
    const count = this.stationRefCounts.get(stationId) ?? 0;
    if (count === 0) {
      const channel = getSupabase()
        .channel(`reports:station:${stationId}`)
        .on("broadcast", { event: "report_added" }, () => {
          queryClient.invalidateQueries({ queryKey: ["station", stationId] });
          queryClient.invalidateQueries({ queryKey: ["reports", stationId] });
        })
        .on("broadcast", { event: "checkin_updated" }, () => {
          queryClient.invalidateQueries({ queryKey: ["check-in-summary", stationId] });
        })
        .on("broadcast", { event: "issue_opened" }, () => {
          queryClient.invalidateQueries({ queryKey: ["issues", "station", stationId] });
        })
        .subscribe(makeStatusHandler({ stationId }));
      this.stationChannels.set(stationId, channel);
    }
    this.stationRefCounts.set(stationId, count + 1);

    return () => {
      const next = (this.stationRefCounts.get(stationId) ?? 1) - 1;
      if (next <= 0) {
        const ch = this.stationChannels.get(stationId);
        this.stationChannels.delete(stationId);
        this.stationRefCounts.delete(stationId);
        if (ch) removeChannelSafe(ch, { stationId });
      } else {
        this.stationRefCounts.set(stationId, next);
      }
    };
  }

  subscribeToCommunityFeed(): () => void {
    if (this.communityFeedRefCount === 0) {
      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["community", "feed"] });
      };
      this.communityFeedChannel = getSupabase()
        .channel("community:feed")
        .on("broadcast", { event: "report_added" }, invalidate)
        .on("broadcast", { event: "checkin_updated" }, invalidate)
        .on("broadcast", { event: "issue_opened" }, invalidate)
        .subscribe(makeStatusHandler({ channel: "community:feed" }));
      this.communityFeedHealthUnsub = healthMonitor.subscribe((unhealthy) => {
        if (!unhealthy) invalidate();
      });
    }
    this.communityFeedRefCount++;

    return () => {
      this.communityFeedRefCount--;
      if (this.communityFeedRefCount <= 0) {
        const ch = this.communityFeedChannel;
        const unsub = this.communityFeedHealthUnsub;
        this.communityFeedChannel = null;
        this.communityFeedHealthUnsub = null;
        this.communityFeedRefCount = 0;
        unsub?.();
        if (ch) removeChannelSafe(ch, { channel: "community:feed" });
      }
    };
  }

  subscribeToFavorites(stationIds: string[]): void {
    const next = new Set(stationIds);

    for (const id of next) {
      if (!this.favoriteUnsubs.has(id)) {
        this.favoriteUnsubs.set(id, this.subscribeToStation(id));
      }
    }
    const gone = [...this.favoriteUnsubs.keys()].filter((id) => !next.has(id));
    for (const id of gone) {
      this.favoriteUnsubs.get(id)?.();
      this.favoriteUnsubs.delete(id);
    }
  }

  private subscribeToCell(cell: string): void {
    const channel = getSupabase()
      .channel(`stations:geohash:${cell}`)
      .on("broadcast", { event: "report_added" }, (payload) => {
        const stationId = payload.payload?.station_id as string | undefined;
        if (stationId) {
          queryClient.invalidateQueries({ queryKey: ["station", stationId] });
          queryClient.invalidateQueries({ queryKey: ["reports", stationId] });
        }
        queryClient.invalidateQueries({ queryKey: ["stations"] });
      })
      .subscribe(makeStatusHandler({ cell }));
    this.cellChannels.set(cell, channel);
  }

  private unsubscribeFromCell(cell: string): void {
    const ch = this.cellChannels.get(cell);
    if (!ch) return;
    this.cellChannels.delete(cell);
    removeChannelSafe(ch, { cell });
  }
}

function makeStatusHandler(ctx: Record<string, unknown>) {
  return (status: string, err?: Error) => {
    if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
      console.warn("[ChannelManager] channel failed", { ...ctx, status, err });
      healthMonitor.onDisconnect();
    } else if (status === "SUBSCRIBED") {
      healthMonitor.onReconnect();
    }
  };
}

function removeChannelSafe(channel: RealtimeChannel, ctx: Record<string, unknown>): void {
  void getSupabase()
    .removeChannel(channel)
    .then((status) => {
      if (status === "error" || status === "timed out") {
        console.warn("[ChannelManager] removeChannel failed", { ...ctx, status });
      }
    })
    .catch((err: unknown) => {
      console.warn("[ChannelManager] removeChannel threw", { ...ctx, err: String(err) });
    });
}

export const channelManager = new ChannelManager();
